import { HomepagePropertiesQuery } from "@/types";
import { Property } from "@/types/property";

import { axiosWithToken } from "@/utils/axios";
import { AxiosResponse } from "axios";

/**
 * Closed sort allowlist for the experimental feed endpoints. Mirrors the
 * backend allowlist in
 * `property.feedfunctions.usecase.experimental.ts` (`resolveFeedSort`) —
 * keep the two in sync. Unknown values are treated as absent (no sort) by
 * the backend, never as errors.
 *
 * - Absent/empty/unknown: legacy behavior (no sort).
 * - `recommended` / `relevance`: explicit spelling of the curated order.
 * - `newest` / `oldest`: plain recency.
 * - `price_desc` / `price_asc`: NGN-normalized effective price
 *   (meaningful only alongside a listingType filter).
 * - `bedrooms_desc` / `bedrooms_asc` / `largest_plot`: room/plot counts.
 */
export type ExperimentalFeedSortKey =
  | "recommended"
  | "newest"
  | "oldest"
  | "price_desc"
  | "price_asc"
  | "bedrooms_desc"
  | "bedrooms_asc"
  | "largest_plot"
  | "relevance";

/**
 * Dropdown options for the sort control. Single source of truth shared by
 * the filter component and the active-filter pills. `""` (Recommended)
 * omits the param, which the backend treats as legacy behavior.
 */
const SORT_ENTRIES: [ExperimentalFeedSortKey | "", string][] = [
  ["", "Recommended"],
  ["newest", "Newest first"],
  ["oldest", "Oldest first"],
  ["price_asc", "Price: Low to High"],
  ["price_desc", "Price: High to Low"],
  ["bedrooms_desc", "Most bedrooms"],
  ["bedrooms_asc", "Fewest bedrooms"],
  ["largest_plot", "Largest plot"],
  ["relevance", "Most relevant"],
];

export const EXPERIMENTAL_SORT_OPTIONS: { value: string; label: string }[] =
  SORT_ENTRIES.map(([value, label]) => ({ value, label }));

export const EXPERIMENTAL_SORT_LABELS: Record<string, string> =
  Object.fromEntries(SORT_ENTRIES);

/**
 * Query params for the experimental feed endpoints. Extends the shared
 * homepage query with `sort` plus the extra filters the backend documents
 * on the managed/deals paths.
 */
export interface ExperimentalFeedQuery extends HomepagePropertiesQuery {
  sort?: ExperimentalFeedSortKey;
  strictNumeric?: boolean;
  minPrice?: string;
  maxPrice?: string;
}

/**
 * Feed metadata returned by the experimental endpoints. Same shape as the
 * legacy feed metadata plus the required `appliedSort` marker:
 * `"default"` (absent/empty/unknown sort), `"recommended"`, or the
 * resolved sort key. Homepage default-branch responses additionally carry
 * the pool/backfill split counts.
 */
export interface ExperimentalFeedMetadata {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  appliedSort: string;
  optimizedCountOnPage?: number;
  fallbackCountOnPage?: number;
}

export interface ExperimentalFeedResponse<T> {
  status: string;
  message: string;
  data: T[];
  metadata: ExperimentalFeedMetadata;
}

class ExperimentalPropertyServiceClass {
  GetHomepageProperties(
    payload: ExperimentalFeedQuery,
    options?: { signal?: AbortSignal },
  ): Promise<AxiosResponse<ExperimentalFeedResponse<Property>>> {
    return axiosWithToken().get("/properties/experimental/homepage", {
      params: payload,
      ...(options?.signal ? { signal: options.signal } : {}),
    });
  }

  GetManagedProperties(
    payload: ExperimentalFeedQuery,
  ): Promise<AxiosResponse<ExperimentalFeedResponse<Property>>> {
    return axiosWithToken().get("/properties/experimental/managed_properties", {
      params: payload,
    });
  }

  GetDeals(
    payload: ExperimentalFeedQuery,
  ): Promise<AxiosResponse<ExperimentalFeedResponse<Property>>> {
    return axiosWithToken().get("/properties/experimental/deals", {
      params: payload,
    });
  }
}

const ExperimentalPropertyService = new ExperimentalPropertyServiceClass();
export default ExperimentalPropertyService;
